;(() => {
  const namespace = (globalThis.StylistLocator = globalThis.StylistLocator || {})

  // ============================================================================
  // Configuration
  // ============================================================================

  const CONFIG = {
    SESSION_KEY: 'sl_session_id',
    DEBUG: true, // Set to false in production
    EVENT_PREFIX: 'stylist_locator_', // Prefix for GA4 event names
  }

  // ============================================================================
  // State
  // ============================================================================

  let sessionId = null

  // ============================================================================
  // Session Management
  // ============================================================================

  /**
   * Generate or retrieve an anonymous session ID.
   * Uses sessionStorage so sessions are unique per browser tab.
   * @returns {string} UUID session identifier
   */
  function getSessionId() {
    if (sessionId) return sessionId

    try {
      sessionId = sessionStorage.getItem(CONFIG.SESSION_KEY)
      if (!sessionId) {
        sessionId = generateUUID()
        sessionStorage.setItem(CONFIG.SESSION_KEY, sessionId)
      }
    } catch (e) {
      // sessionStorage not available, generate ephemeral ID
      sessionId = generateUUID()
    }

    return sessionId
  }

  /**
   * Generate a UUID v4 compatible identifier.
   * @returns {string} UUID string
   */
  function generateUUID() {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
      return crypto.randomUUID()
    }
    // Fallback for older browsers
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0
      const v = c === 'x' ? r : (r & 0x3) | 0x8
      return v.toString(16)
    })
  }

  function logDebug(...args) {
    if (CONFIG.DEBUG) {
      console.log(...args)
    }
  }

  function logWarn(...args) {
    if (CONFIG.DEBUG) {
      console.warn(...args)
    }
  }

  function logError(...args) {
    if (CONFIG.DEBUG) {
      console.error(...args)
    }
  }

  // ============================================================================
  // Google Analytics Integration
  // ============================================================================

  /**
   * Check if Google Analytics (gtag) is available.
   * @returns {boolean}
   */
  function isGtagAvailable() {
    return typeof gtag === 'function'
  }


  /**
   * Send an event to Google Analytics 4 only.
   *
   * @param {string} eventName - GA4 event name
   * @param {Object} params - Event parameters
   */
  function sendToGA(eventName, params) {
    const fullEventName = CONFIG.EVENT_PREFIX + eventName

    logDebug('[Analytics] Sending to GA4:', fullEventName, params)

    // Only use gtag for GA4 - no fallback to dataLayer/Google Ads
    if (isGtagAvailable()) {
      try {
        gtag('event', fullEventName, params)
        logDebug('[Analytics] Event sent to GA4 via gtag()')
        return true
      } catch (e) {
        logError('[Analytics] gtag() failed:', e)
      }
    }

    logWarn('[Analytics] GA4 gtag() not available - event not sent')
    return false
  }

  // ============================================================================
  // Event Tracking
  // ============================================================================

  /**
   * Track a stylist-related event.
   * Sends event directly to Google Analytics.
   *
   * @param {string} eventType - Event type (e.g., 'click_list', 'stylist_view')
   * @param {string} stylistEmail - The stylist's email (unique identifier)
   * @param {string} [stylistName] - The stylist's full name
   * @param {Object} [metadata={}] - Additional event metadata
   */
  function track(eventType, stylistEmail, stylistName, metadata = {}) {
    if (!eventType || !stylistEmail) {
      logWarn('[Analytics] track() called with missing eventType or stylistEmail')
      return
    }

    const params = {
      stylist_email: stylistEmail,
      stylist_name: stylistName || null,
      session_id: getSessionId(),
      ...metadata,
    }

    sendToGA(eventType, params)
  }

  /**
   * Track a search event with location and results data.
   *
   * @param {Object} searchData - Search event data
   * @param {string} [searchData.search_query] - The search query string
   * @param {string} [searchData.search_type] - Type of search ('location', 'stylist_name', 'geocode')
   * @param {number} [searchData.latitude] - Search center latitude
   * @param {number} [searchData.longitude] - Search center longitude
   * @param {number} [searchData.radius_miles] - Search radius in miles
   * @param {number} [searchData.results_count] - Number of results returned
   */
  function trackSearch(searchData) {
    const params = {
      session_id: getSessionId(),
      ...searchData,
    }

    sendToGA('search', params)
  }

  /**
   * Track multiple stylists appearing in search results.
   * Sends a single event with all stylist emails and names to avoid event spam.
   *
   * @param {Array<Object>} stylists - Array of stylist objects that appeared
   * @param {string} searchQuery - The search query that produced these results
   */
  function trackSearchAppearances(stylists, searchQuery) {
    if (!Array.isArray(stylists) || stylists.length === 0) return

    // Send a single event with all stylists that appeared
    // This avoids sending too many events for a single search
    const params = {
      session_id: getSessionId(),
      search_query: searchQuery,
      stylists_shown: stylists.map((s) => s.email).filter(Boolean).join(','),
      stylist_names_shown: stylists.map((s) => s.name).filter(Boolean).join(','),
      results_count: stylists.length,
    }

    sendToGA('search_results', params)
  }

  // ============================================================================
  // Initialization
  // ============================================================================

  function init() {
    const hasGtag = isGtagAvailable()
    logDebug('[Analytics] Module initialized')
    logDebug('[Analytics] GA4 gtag available:', hasGtag)

    if (!hasGtag) {
      logWarn('[Analytics] GA4 gtag not available. Events will be logged but not sent.')
    }
  }

  init()

  // ============================================================================
  // Debug Utilities
  // ============================================================================

  /**
   * Enable or disable debug mode.
   * @param {boolean} enabled - Whether to enable debug logging
   */
  function setDebug(enabled) {
    CONFIG.DEBUG = !!enabled
    console.log('[Analytics] Debug mode:', CONFIG.DEBUG ? 'enabled' : 'disabled')
  }

  /**
   * Test the analytics integration by sending a test event.
   */
  function testEvent() {
    track('test', 'test@example.com', 'Test Stylist', { test: true })
    console.log('[Analytics] Test event sent. Check your GA4 DebugView or browser network tab.')
  }

  // ============================================================================
  // Exports
  // ============================================================================

  namespace.analytics = {
    track,
    trackSearch,
    trackSearchAppearances,
    getSessionId,
    // Debug utilities
    setDebug,
    testEvent,
  }
})()
